import { isIDF, IDF_GROUP_VALUE, IDF_GROUP_LABEL } from './idfCommunes';
import { getCityGroup, CITY_GROUPS } from './cityGroups';

// ── Nettoyage & extraction depuis les champs offre ───────────────────────────

/** Supprime toute mention "· via Plateforme" du champ location. */
export function cleanLocation(location: string): string {
  return (location || '')
    .replace(/\s*[·•]\s*via\s+[^·•\n]+/gi, '')
    .replace(/\s*via\s+[^·•\n]+/gi, '')
    .replace(/\s*[·•]\s*$/, '')
    .trim();
}

/** Extrait uniquement le nom de ville (premier segment, sans "via Platform"). */
export function extractCity(location: string): string {
  const withoutVia = (location || '')
    .replace(/\s*[·•|]\s*via\s[^·•,]*/gi, '')
    .replace(/\s*via\s[^·•,]*/gi, '')
    .trim();
  return withoutVia.split(/[,·•]/)[0].trim();
}

// ── Type de contrat (depuis le champ backend type_contrat_detail) ─────────────
/** Retourne le type de contrat d'une offre, avec fallback 'Non spécifié'. */
export function getContrat(offre: { type_contrat_detail?: string }): string {
  return offre.type_contrat_detail?.trim() || 'Non spécifié';
}

// ── Logos & initiales intelligentes ──────────────────────────────────────────
export const VIA_PATTERN_TO_KEY: [RegExp, string][] = [
  [/via\s+linkedin/i,                    'linkedin'],
  [/via\s+jobteaser/i,                   'jobteaser'],
  [/via\s+hellowork/i,                   'hellowork'],
  [/via\s+welcome\s+to\s+the\s+jungle/i, 'wttj'],
  [/via\s+indeed/i,                      'indeed'],
  [/via\s+glassdoor/i,                   'glassdoor'],
  [/via\s+michael\s+page/i,              'michaelpage'],
  [/via\s+wizbii/i,                      'wizbii'],
  [/via\s+apec/i,                        'apec'],
  [/via\s+jobijoba/i,                    'jobijoba'],
  [/via\s+adzuna/i,                      'adzuna'],
  [/via\s+cadremploi/i,                  'cadremploi'],
  [/via\s+france\s+travail/i,            'france_travail'],
];

export interface SourceInfo { logo: string; label: string }

/** Calcule les initiales d'un nom : "BNP Paribas" → "BP", "LinkedIn" → "Li" */
export function computeInitials(name: string): string {
  const cleaned = name.replace(/[_-]/g, ' ').trim();
  const words = cleaned.split(/\s+/).filter(w => w.length > 0);
  if (words.length === 0) return '??';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  // Multi-mots : première lettre de chaque mot (max 3)
  return words.slice(0, 3).map(w => w[0]).join('').toUpperCase();
}

export type BadgeInfo =
  | { type: 'logo'; logo: string; label: string }
  | { type: 'initials'; text: string; label: string };

/**
 * Retourne soit un logo soit des initiales pour afficher la source d'une offre.
 * Priorité : logo direct → logo via pattern → initiales "via X" → initiales source.
 */
export function getOfferBadge(
  offre: { source?: string; location?: string },
  SOURCE_LOGO: Record<string, SourceInfo>
): BadgeInfo {
  const srcKey = (offre.source || '').toLowerCase();
  const GOOGLE_SOURCES = ['google', 'google_jobs', ''];

  // Cas spécial : efinancialcareers → affiché comme « site entreprise »
  if (srcKey === 'efinancial' || srcKey === 'efinancialcareers') {
    return { type: 'initials', text: 'SE', label: 'site entreprise' };
  }

  // 1. Match direct hors Google
  if (!GOOGLE_SOURCES.includes(srcKey)) {
    const direct = SOURCE_LOGO[srcKey];
    if (direct) return { type: 'logo', ...direct };
  }

  const loc = offre.location || '';

  // 2. Détecter un portail connu dans le texte "via X"
  for (const [pattern, key] of VIA_PATTERN_TO_KEY) {
    if (pattern.test(loc)) {
      const info = SOURCE_LOGO[key];
      if (info) return { type: 'logo', ...info };
    }
  }

  // 3. Extraire le premier "via NomPlateforme" non-Google pour les initiales
  const viaRe = /via\s+([^·•,\n(]+)/gi;
  let viaM: RegExpExecArray | null;
  while ((viaM = viaRe.exec(loc)) !== null) {
    const platform = viaM[1].trim().replace(/\s+/g, ' ');
    if (!platform.toLowerCase().includes('google')) {
      return { type: 'initials', text: computeInitials(platform), label: platform };
    }
  }

  // 4. Fallback : initiales de la source brute (ou "??" si vide/google)
  const label = srcKey || '?';
  if (GOOGLE_SOURCES.includes(srcKey)) {
    return { type: 'initials', text: '??', label: 'Inconnu' };
  }
  return { type: 'initials', text: computeInitials(label), label };
}

/** Compat legacy — retourne uniquement SourceInfo | null */
export function getBestSourceInfo(
  offre: { source?: string; location?: string },
  SOURCE_LOGO: Record<string, SourceInfo>
): SourceInfo | null {
  const badge = getOfferBadge(offre, SOURCE_LOGO);
  return badge.type === 'logo' ? { logo: badge.logo, label: badge.label } : null;
}

// ── Date absolue DD/MM/YYYY ───────────────────────────────────────────────────
export function formatRelative(date: string): string {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return date;
  const day   = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year  = d.getFullYear();
  return `${day}/${month}/${year}`;
}

// ── Options villes & contrats pour les dropdowns ──────────────────────────────
export interface SelectOption { label: string; value: string }

export function buildVilleOptions(offres: { location?: string }[]): SelectOption[] {
  const counts: Record<string, number> = {};
  let idfCount = 0;
  const groupCounts: Record<string, number> = {};

  for (const o of offres) {
    const ville = extractCity(o.location || '');
    if (!ville) continue;
    if (isIDF(ville)) {
      idfCount++;
    } else {
      const group = getCityGroup(ville);
      if (group) {
        groupCounts[group.value] = (groupCounts[group.value] || 0) + 1;
      } else {
        counts[ville] = (counts[ville] || 0) + 1;
      }
    }
  }

  const options: SelectOption[] = [];

  // Option groupée IDF en tête si au moins une offre IDF
  if (idfCount > 0) {
    options.push({
      label: `${IDF_GROUP_LABEL} (${idfCount})`,
      value: IDF_GROUP_VALUE,
    });
  }

  // Options groupées par grande ville (dans l'ordre défini)
  for (const group of CITY_GROUPS) {
    const n = groupCounts[group.value] || 0;
    if (n > 0) {
      options.push({ label: `${group.label} (${n})`, value: group.value });
    }
  }

  // Villes hors groupes triées par fréquence décroissante
  const hors_groupes = Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .map(([v, n]) => ({ label: `${v} (${n})`, value: v }));

  return options.concat(hors_groupes);
}

export function buildContratOptions(offres: { type_contrat_detail?: string }[]): SelectOption[] {
  const counts: Record<string, number> = {};
  for (const o of offres) {
    const type = getContrat(o);
    counts[type] = (counts[type] || 0) + 1;
  }
  const order = ['Alternance', 'Stage', 'CDI/CDD', 'Non spécifié'];
  return order.filter(t => counts[t] !== undefined)
    .map(t => ({ label: `${t} (${counts[t]})`, value: t }));
}
